import React, { useState, useRef, useEffect } from 'react';
import COLORS from '../../utils/colors';
import { sendMessageToChatbot } from '../../services/chatbotService';
import Cookies from 'js-cookie';
import { getUserId } from '../../services/authService';
import { FaRobot, FaTimes, FaTrash, FaPaperPlane, FaSpinner, FaUser } from 'react-icons/fa';
import './chatbox.css';
import { useTheme } from '../../context/ThemeContext';

interface Message {
  id: number;
  text: string;
  sender: 'user' | 'system';
  timestamp: Date;
}

// Prefix cho cookie
const CHAT_HISTORY_COOKIE_PREFIX = 'strava_chat_history_';
// Số lượng tin nhắn tối đa lưu trong cookie
const MAX_STORED_MESSAGES = 30;

const ChatBox: React.FC = () => {
  console.log('ChatBox rendering');
  
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: 'Xin chào! Tôi có thể giúp gì cho bạn?',
      sender: 'system',
      timestamp: new Date()
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { theme } = useTheme();
  
  // Lấy ID của người dùng hiện tại
  useEffect(() => {
    const currentUserId = getUserId();
    console.log('ChatBox: Current User ID', currentUserId);
    setUserId(currentUserId);
  }, []);
  
  // Focus vào input khi mở chat box
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpen]);
  
  // Tạo tên cookie dựa trên ID người dùng
  const getCookieName = (): string => {
    return userId ? `${CHAT_HISTORY_COOKIE_PREFIX}${userId}` : CHAT_HISTORY_COOKIE_PREFIX;
  };
  
  // Cuộn xuống cuối khi mở chat box
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, [isOpen]);
  
  // Đọc lịch sử chat từ cookie khi component mount hoặc khi userId thay đổi
  useEffect(() => {
    if (userId) {
      const savedMessages = loadChatHistory();
      if (savedMessages && savedMessages.length > 0) {
        setMessages(savedMessages);
      }
    }
  }, [userId]);
  
  // Lưu lịch sử chat vào cookie mỗi khi messages thay đổi
  useEffect(() => {
    // Chỉ lưu khi đã có userId và khi messages có nhiều hơn tin nhắn chào mừng
    if (userId && messages.length > 1) {
      saveChatHistory(messages);
    }
  }, [messages, userId]);
  
  // Cuộn xuống cuối danh sách tin nhắn khi có tin nhắn mới
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);
  
  // Hàm lưu lịch sử chat vào cookie
  const saveChatHistory = (chatMessages: Message[]) => {
    try {
      // Chỉ lưu tối đa MAX_STORED_MESSAGES tin nhắn gần nhất
      const messagesToStore = chatMessages.slice(-MAX_STORED_MESSAGES);
      
      // Chuyển đổi Date objects thành string trước khi lưu
      const serializableMessages = messagesToStore.map(msg => ({
        ...msg,
        timestamp: msg.timestamp.toISOString()
      }));
      
      // Lưu vào cookie (hết hạn sau 7 ngày)
      Cookies.set(getCookieName(), JSON.stringify(serializableMessages), { 
        expires: 7, 
        sameSite: 'strict',
        secure: window.location.protocol === 'https:'
      });
      
    } catch (error) {
      console.error('Lỗi khi lưu lịch sử chat:', error);
    }
  };
  
  // Hàm đọc lịch sử chat từ cookie
  const loadChatHistory = (): Message[] | null => {
    try {
      const savedHistory = Cookies.get(getCookieName());
      
      if (!savedHistory) {
        return null;
      }
      
      // Parse JSON và chuyển đổi lại timestamp thành Date objects
      const parsedMessages = JSON.parse(savedHistory);
      return parsedMessages.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }));
      
    } catch (error) {
      console.error('Lỗi khi đọc lịch sử chat:', error);
      return null;
    }
  };
  
  // Hàm xóa lịch sử chat
  const clearChatHistory = () => {
    try {
      Cookies.remove(getCookieName());
      setMessages([
        {
          id: 1,
          text: 'Xin chào! Tôi có thể giúp gì cho bạn?',
          sender: 'system',
          timestamp: new Date()
        }
      ]);
    } catch (error) {
      console.error('Lỗi khi xóa lịch sử chat:', error);
    }
  };
  
  const toggleChat = () => {
    setIsOpen(!isOpen);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() === '') return;
    
    // Thêm tin nhắn của người dùng
    const newUserMessage: Message = {
      id: messages.length + 1,
      text: message,
      sender: 'user',
      timestamp: new Date()
    };
    
    // Lưu tin nhắn người dùng và reset form
    const userMessageText = message;
    setMessages(prev => [...prev, newUserMessage]);
    setMessage('');
    setIsLoading(true);
    setIsTyping(true);
    
    try {
      // Gọi API chatbot thông qua service
      const botResponse = await sendMessageToChatbot(userMessageText);
      
      // Thêm phản hồi từ chatbot
      const systemReply: Message = {
        id: messages.length + 2,
        text: botResponse,
        sender: 'system',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, systemReply]);
    } catch (error) {
      console.error('Lỗi khi xử lý tin nhắn:', error);
      
      // Tin nhắn lỗi
      const errorMessage: Message = {
        id: messages.length + 2,
        text: 'Có lỗi xảy ra. Vui lòng thử lại sau.',
        sender: 'system',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };
  
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('vi-VN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <div className="chat-container">
      {/* Chat button */}
      <button
        onClick={toggleChat}
        className="chat-button"
        aria-label={isOpen ? "Đóng hỗ trợ trực tuyến" : "Mở hỗ trợ trực tuyến"}
      >
        {isOpen ? <FaTimes /> : <FaRobot />}
      </button>
      
      {/* Chat box */}
      {isOpen && (
        <div className={`chat-box ${theme}`}>
          {/* Chat header */}
          <div className="chat-header">
            <div className="chat-title">
              <FaRobot />
              <span>Trợ giúp</span>
            </div>
            <div className="chat-actions">
              <button
                onClick={clearChatHistory}
                title="Xóa lịch sử chat"
                className="chat-action-button"
              >
                <FaTrash />
              </button>
              <button
                onClick={toggleChat}
                title="Đóng chat"
                className="chat-action-button"
              >
                <FaTimes />
              </button>
            </div>
          </div>
          
          {/* Messages container */}
          <div className="chat-messages">
            {messages.map((msg) => (
              <div 
                key={msg.id} 
                className={`message ${msg.sender === 'user' ? 'message-user' : 'message-system'}`}
              >
                <div className="message-content">
                  {msg.text}
                </div>
                <div className="message-time">
                  {formatTime(msg.timestamp)}
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="typing-indicator">
                <div className="typing-dot"></div>
                <div className="typing-dot"></div>
                <div className="typing-dot"></div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Chat input */}
          <form onSubmit={handleSubmit} className="chat-input-container">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Nhập tin nhắn..."
              className="chat-input"
              ref={inputRef}
              disabled={isLoading}
            />
            <button 
              type="submit" 
              className="chat-send-button"
              disabled={isLoading || message.trim() === ''}
            >
              {isLoading ? <FaSpinner className="loading-icon" /> : <FaPaperPlane />}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatBox; 